if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        const thumbnailsContainer =
          this.querySelector('[data-thumbnail-container]') || this.querySelector('[id^="GalleryThumbnails"]');

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: thumbnailsContainer,
        };
        this.fractionWrap = this.querySelector('[data-gallery-fraction]');
        this.fractionCurrent = this.fractionWrap?.querySelector('[data-gallery-current]');
        this.fractionTotal = this.fractionWrap?.querySelector('[data-gallery-total]');
        this.progressbarWrap = this.querySelector('[data-gallery-progressbar]');
        this.progressDrag = this.progressbarWrap?.querySelector('[data-gallery-progress-drag]');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.viewerSlides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        this.dotsContainer = this.querySelector('[data-media-dots]');
        this.dotButtons = Array.from(this.dotsContainer?.querySelectorAll('[data-media-position]') || []);

        if (this.elements.viewer) {
          // Make snap updates immediate (no 500ms delay)
          this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

          // Real-time progress update while swiping (rAF-throttled)
          const slider = this.elements.viewer.querySelector('.slider');
          if (slider && this.progressDrag) {
            this._viewerScrollRaf = null;
            this._viewerScrollStep = null;
            this._viewerScrollStepWidth = null;

            const onScroll = () => {
              if (this._viewerScrollRaf) return;
              this._viewerScrollRaf = requestAnimationFrame(() => {
                this._viewerScrollRaf = null;
                this.updateProgressBarRealtime(slider);
              });
            };

            slider.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener(
              'resize',
              () => {
                // step depends on layout width/gap
                this._viewerScrollStep = null;
                this._viewerScrollStepWidth = null;
                this.updateProgressBarRealtime(slider);
              },
              { passive: true }
            );
          }
        }
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const thumbnailButton = mediaToSwitch.querySelector('button');
            const targetId = mediaToSwitch.dataset.target;
            thumbnailButton.addEventListener('click', (event) => this.onThumbnailClick(event, targetId));
            this.registerDesktopHoverHandlers(thumbnailButton, targetId);
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        if (this.dataset.desktopLayout === 'stacked') {
          this.viewerSlides.forEach((slide) => this.registerViewerHoverHandlers(slide));
        }

        if (this.dotButtons.length) {
          this.dotButtons.forEach((dot) =>
            dot.addEventListener('click', (event) => this.onDotClick(event, dot))
          );
        }
        this.updatePaginationDots(this.viewerSlides[0]?.dataset?.mediaId);

        this.mql.addEventListener('change', this.onViewportChange.bind(this));
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        this.updatePaginationDots(mediaId);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const shouldScroll = options.shouldScroll === true;
        this.viewerSlides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!shouldScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        this.updatePaginationDots(mediaId);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const sliderComponent = thumbnail.closest('slider-component');
        if (sliderComponent?.isSlideVisible && sliderComponent.isSlideVisible(thumbnail, 10)) return;
        if (!this.mql.matches) return;

        this.scrollThumbnailIntoView(thumbnail);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      onThumbnailHover(mediaId) {
        if (!this.mql.matches) return;
        this.setActiveMedia(mediaId, false, { shouldScroll: false });
      }

      onThumbnailClick(event, mediaId) {
        event.preventDefault();
        this.setActiveMedia(mediaId, false);
      }

      onDotClick(event, dot) {
        event.preventDefault();
        const targetPosition = dot?.dataset?.mediaPosition;
        if (!targetPosition) return;
        this.viewerSlides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        const targetSlide = this.viewerSlides.find((slide) => slide.dataset.mediaPosition === targetPosition);
        if (!targetSlide) return;
        this.setActiveMedia(targetSlide.dataset.mediaId, false);
      }

      registerDesktopHoverHandlers(thumbnailButton, targetId) {
        const mouseEnterHandler = this.onThumbnailHover.bind(this, targetId);
        const focusHandler = this.onThumbnailHover.bind(this, targetId);

        const installHandlers = () => {
          thumbnailButton.addEventListener('mouseenter', mouseEnterHandler);
          thumbnailButton.addEventListener('focus', focusHandler);
        };

        const removeHandlers = () => {
          thumbnailButton.removeEventListener('mouseenter', mouseEnterHandler);
          thumbnailButton.removeEventListener('focus', focusHandler);
        };

        if (this.mql.matches) {
          installHandlers();
        }

        thumbnailButton.dataset.hoverHandlersInstalled = this.mql.matches;

        thumbnailButton.addEventListener('mediaGalleryHoverToggle', (event) => {
          if (event.detail.shouldEnable && thumbnailButton.dataset.hoverHandlersInstalled !== 'true') {
            installHandlers();
            thumbnailButton.dataset.hoverHandlersInstalled = 'true';
          } else if (!event.detail.shouldEnable && thumbnailButton.dataset.hoverHandlersInstalled === 'true') {
            removeHandlers();
            thumbnailButton.dataset.hoverHandlersInstalled = 'false';
          }
        });
      }

      registerViewerHoverHandlers(viewerSlide) {
        if (!viewerSlide?.dataset?.mediaId) return;
        const targetId = viewerSlide.dataset.mediaId;
        const mouseEnterHandler = this.onThumbnailHover.bind(this, targetId);
        const focusHandler = this.onThumbnailHover.bind(this, targetId);

        const installHandlers = () => {
          viewerSlide.addEventListener('mouseenter', mouseEnterHandler);
          viewerSlide.addEventListener('focusin', focusHandler);
        };

        const removeHandlers = () => {
          viewerSlide.removeEventListener('mouseenter', mouseEnterHandler);
          viewerSlide.removeEventListener('focusin', focusHandler);
        };

        if (this.mql.matches) {
          installHandlers();
        }

        viewerSlide.dataset.viewerHoverHandlersInstalled = this.mql.matches;

        viewerSlide.addEventListener('mediaGalleryHoverToggle', (event) => {
          if (event.detail.shouldEnable && viewerSlide.dataset.viewerHoverHandlersInstalled !== 'true') {
            installHandlers();
            viewerSlide.dataset.viewerHoverHandlersInstalled = 'true';
          } else if (!event.detail.shouldEnable && viewerSlide.dataset.viewerHoverHandlersInstalled === 'true') {
            removeHandlers();
            viewerSlide.dataset.viewerHoverHandlersInstalled = 'false';
          }
        });
      }

      onViewportChange(event) {
        const shouldEnable = event.matches;
        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target] button')
            .forEach((button) =>
              button.dispatchEvent(
                new CustomEvent('mediaGalleryHoverToggle', {
                  detail: { shouldEnable },
                })
              )
            );
        }

        if (this.dataset.desktopLayout === 'stacked' && this.viewerSlides.length) {
          this.viewerSlides.forEach((slide) =>
            slide.dispatchEvent(
              new CustomEvent('mediaGalleryHoverToggle', {
                detail: { shouldEnable },
              })
            )
          );
        }
      }

      updatePaginationDots(mediaId) {
        if (!mediaId) return;

        this.viewerSlides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        const activeSlide = this.viewerSlides.find((slide) => slide.dataset.mediaId === mediaId) || this.viewerSlides[0];
        if (!activeSlide) return;

        // IMPORTANT: Fraction/progress should reflect the *media index* (1..N),
        // not slider-component "pages" (which can differ on peek/columns layouts).
        const fallbackIndex = Math.max(0, this.viewerSlides.indexOf(activeSlide)) + 1;
        const activePosition = Number(activeSlide.dataset.mediaPosition) || fallbackIndex;
        const total = Number(this.fractionTotal?.textContent) || this.viewerSlides.length || 1;

        if (this.dotButtons.length) {
          this.dotButtons.forEach((dot) => {
            if (Number(dot.dataset.mediaPosition) === activePosition) {
              dot.classList.add('is-active');
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.classList.remove('is-active');
              dot.removeAttribute('aria-current');
            }
          });
        }

        // update fraction + progress bar
        if (this.fractionCurrent) this.fractionCurrent.textContent = String(activePosition);
        if (this.fractionTotal) this.fractionTotal.textContent = String(total);
        if (this.progressDrag) {
          const dragWidth = 100 / total;
          const left = (activePosition - 1) * dragWidth;
          this.progressDrag.style.width = `${dragWidth}%`;
          this.progressDrag.style.left = `${left}%`;
        }
      }

      // Real-time progress bar update based on scroll position (no delay)
      updateProgressBarRealtime(slider) {
        if (!this.progressDrag || !slider) return;

        // IMPORTANT: don't use scrollLeft/maxScroll for index on "peek" sliders (it overestimates).
        // Use actual slide step (offsetLeft delta) so 2nd image is always 2/N, etc.
        const slides = Array.from(slider.querySelectorAll('.slider__slide'));
        if (slides.length <= 1) return;

        // Source of truth for N is the liquid-rendered total (media_count),
        // so we never end up with impossible values like 12/6.
        const total = Number(this.fractionTotal?.textContent) || slides.length || 1;
        if (total <= 1) return;

        // Use slider-component's computed step (more reliable than offsetLeft on grid/peek layouts)
        const stepPx = Number(this.elements.viewer?.sliderItemOffset) || (slides[1]?.offsetLeft || 0) - (slides[0]?.offsetLeft || 0) || 1;
        const rawIndex = slider.scrollLeft / stepPx + 1; // 1-based, float while swiping
        const clampedIndex = Math.max(1, Math.min(total, rawIndex));

        const dragWidth = 100 / total;
        const maxLeft = 100 - dragWidth;
        const left = Math.max(0, Math.min(maxLeft, (clampedIndex - 1) * dragWidth));

        this.progressDrag.style.width = `${dragWidth}%`;
        this.progressDrag.style.left = `${left}%`;

        // Update fraction in sync (nearest slide while swiping)
        const currentSlide = Math.max(1, Math.min(total, Math.round(clampedIndex)));
        if (this.fractionCurrent) this.fractionCurrent.textContent = String(currentSlide);
      }

      scrollThumbnailIntoView(thumbnail) {
        const sliderComponent = thumbnail?.closest('slider-component');
        if (!sliderComponent?.slider) return;
        const thumbnailList = thumbnail.closest('ul.thumbnail-list');
        if (thumbnailList?.dataset.grouped === 'true') return;
        const slider = sliderComponent.slider;
        const isVertical = sliderComponent.dataset.orientation === 'vertical';
        const scrollOptions = { behavior: 'smooth' };
        if (isVertical) {
          const offsetTop = thumbnail.offsetTop - slider.offsetTop;
          scrollOptions.top = Math.max(0, offsetTop);
        } else {
          const offsetLeft = thumbnail.offsetLeft - slider.offsetLeft;
          scrollOptions.left = Math.max(0, offsetLeft);
        }
        slider.scrollTo(scrollOptions);
      }
    }
  );
}

function groupThumbnailSlides() {
  const lists = document.querySelectorAll('[id^="Slider-Thumbnails-"]');
  lists.forEach((list) => {
    if (list.dataset.grouped === 'true') return;

    const thumbnails = Array.from(list.querySelectorAll('li.thumbnail-list__item'));

    const parentSliderId = list.id.replace('Slider-', ''); // keep suffix
    const sectionKey = parentSliderId || 'thumbs';
    let groupIndex = 0;
    const newSlides = [];

    const chunks = [];
    if (thumbnails.length <= 6) {
      chunks.push(thumbnails.splice(0));
    } else {
      while (thumbnails.length) {
        chunks.push(thumbnails.splice(0, 6));
      }
    }

    chunks.forEach((chunk) => {
      const page = document.createElement('li');
      page.className = 'thumbnail-list__page slider__slide slider-custom';
      page.id = `Slide-${sectionKey}-page-${groupIndex}`;

      const grid = document.createElement('div');
      grid.className = 'thumbnail-grid';

      chunk.forEach((thumb) => {
        thumb.removeAttribute('id');
        thumb.classList.remove('slider__slide');
        grid.appendChild(thumb);
      });

      page.appendChild(grid);
      newSlides.push(page);
      groupIndex += 1;
    });

    list.innerHTML = '';
    newSlides.forEach((slide) => list.appendChild(slide));
    list.dataset.grouped = 'true';

    const sliderComponent = list.closest('slider-component');
    if (sliderComponent?.resetPages) sliderComponent.resetPages();
  });
}

function initThumbnailGrouping() {
  groupThumbnailSlides();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThumbnailGrouping);
  window.addEventListener('load', initThumbnailGrouping);
} else {
  initThumbnailGrouping();
}

document.addEventListener('shopify:section:load', initThumbnailGrouping);
window.addEventListener('resize', () => {
  const needsGrouping = Array.from(document.querySelectorAll('[id^="Slider-Thumbnails-"]')).some(
    (list) => list.dataset.grouped !== 'true'
  );
  if (needsGrouping) groupThumbnailSlides();
});

function initThumbnailArrows() {
  const arrowButtons = Array.from(document.querySelectorAll('.thumbnail-slider__arrows button'));
  const computeScrollDelta = (slider, stepCount) => {
    const [firstItem] = slider.querySelectorAll('.thumbnail-list__item');
    if (!firstItem) return 0;
    const sliderStyles = window.getComputedStyle(slider);
    const gap = parseFloat(sliderStyles.gap) || 0;
    return (firstItem.offsetWidth + gap) * stepCount;
  };

  arrowButtons.forEach((button) => {
    const arrowSlider = button.closest('slider-component');
    if (arrowSlider && arrowSlider.dataset.thumbnailArrowInit !== 'true') {
      arrowSlider.dataset.thumbnailArrowInit = 'true';
      const slider = arrowSlider.querySelector('.thumbnail-list');
      const prevButton = arrowSlider.querySelector('.thumbnail-slider__arrows .slider-button--prev');
      const nextButton = arrowSlider.querySelector('.thumbnail-slider__arrows .slider-button--next');
      if (slider && (prevButton || nextButton)) {
        let rafId = null;
        const updateArrowState = () => {
          if (rafId) return;
          rafId = requestAnimationFrame(() => {
            rafId = null;
            const isVertical =
              slider.scrollHeight > slider.clientHeight + 1 && slider.scrollWidth <= slider.clientWidth + 1;
            const scrollPosition = isVertical ? slider.scrollTop : slider.scrollLeft;
            const maxScroll = isVertical
              ? Math.max(0, slider.scrollHeight - slider.clientHeight)
              : Math.max(0, slider.scrollWidth - slider.clientWidth);

            if (prevButton) {
              prevButton.classList.toggle('slider-gallery-hide', scrollPosition <= 2);
            }
            if (nextButton) {
              nextButton.classList.toggle('slider-button--loop-back', maxScroll > 0 && scrollPosition >= maxScroll - 2);
            }
          });
        };

        updateArrowState();
        slider.addEventListener('scroll', updateArrowState, { passive: true });
        window.addEventListener('resize', updateArrowState);
      }
    }

    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const slider = arrowSlider?.querySelector('.thumbnail-list');
        if (!slider) return;

        const stepCount = Number(button.dataset.step) || 1;
        const delta = computeScrollDelta(slider, stepCount);
        if (!delta) return;

        const direction = button.name === 'previous' ? -1 : 1;
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        let target = slider.scrollLeft + direction * delta;

        if (direction === 1 && arrowSlider?.dataset.loop === 'true' && slider.scrollLeft >= maxScroll - 2) {
          target = 0;
        } else if (direction === -1 && arrowSlider?.dataset.loop === 'true' && slider.scrollLeft <= 2) {
          target = maxScroll;
        } else {
          target = Math.max(0, Math.min(target, maxScroll));
        }

        slider.scrollTo({
          left: target,
          behavior: 'smooth',
        });
      },
      true
    );
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThumbnailArrows);
} else {
  initThumbnailArrows();
}
